import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { API } from '@/api'
import { Volume } from '@/api/type-server'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { EmptyView, Loading, Trans } from '@/components'
import { useDomain } from '@/contexts/DomainContext'
import { formatDomain } from '@/utils/formatter'

import { VolumeCard } from '../components/VolumeCard'

export function MemberDataDetailPage() {
  const { memberId } = useParams<{ memberId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { domainInfos } = useDomain()

  // Get member info from navigation state
  const memberName = (location.state as any)?.memberName || memberId
  const ownerName = (location.state as any)?.ownerName || memberName

  const [loading, setLoading] = useState(true)
  const [datadriveVolumes, setDatadriveVolumes] = useState<Volume[]>([])

  // Redirect if no memberId
  useEffect(() => {
    if (!memberId) {
      navigate(-1)
    }
  }, [memberId, navigate])

  // Fetch member volumes
  const fetchMemberVolumes = async () => {
    if (!memberId) return

    setLoading(true)
    try {
      const res = await API.getMemberVolumes({ res_id: memberId })

      if (res?.volumes?.subParts) {
        // Filter type=1 volumes (Datadrive)
        const datadriveVols = res.volumes.subParts.filter((v: Volume) => v.type === 1)
        setDatadriveVolumes(datadriveVols)
      }
    } catch (error) {
      console.error('Failed to fetch member volumes:', error)
    } finally {
      setLoading(false)
    }
  }

  // Initial load
  useEffect(() => {
    fetchMemberVolumes()
  }, [memberId])

  // Refresh handler
  const handleRefresh = () => {
    fetchMemberVolumes()
  }

  // Get domain name by domain ID
  const getDomainName = (domain: number): string => {
    return domainInfos.find((info) => info.domain === domain)?.displayName || formatDomain(domain)
  }

  return (
    <div className="card bg-panel-white p-8 flex flex-col gap-8 h-full overflow-y-auto">
      {/* Header: Back Button + Breadcrumb + Refresh Button */}
      <div className="flex items-center justify-between border-b border-neutral-200 pb-4">
        <div className="flex items-center gap-3">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="p-1 bg-panel-gray-100 rounded-md hover:bg-panel-gray-200 transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6 text-tertiary" />
          </button>

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 h6">
            <span className="text-tertiary">
              <Trans i18nKey="teamData.memberDetail.breadcrumb.datadrive">Datadrive</Trans>
            </span>
            <span className="text-primary">/</span>
            <span className="text-primary">{memberName}</span>
          </div>
        </div>

        {/* Refresh Button */}
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="bg-panel-gray-100 px-4 py-3 rounded-lg hover:bg-panel-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="t-sb text-primary">
            <Trans i18nKey="common.refresh">Refresh</Trans>
          </span>
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {datadriveVolumes.length === 0 ? (
            <div className="mt-4">
              <EmptyView
                title={
                  <span className="t-sm text-secondary">
                    <Trans i18nKey="common.emptyViewNoDataMsg" />
                  </span>
                }
              />
            </div>
          ) : (
            datadriveVolumes.map((volume) => (
              <VolumeCard
                key={volume.id}
                volume={volume}
                ownerName={ownerName}
                domainName={getDomainName(volume.domain)}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}
